import React, { useEffect, useRef, useState } from 'react';
import {
  createChart,
  CrosshairMode,
  IChartApi,
  Time,
  ISeriesApi,
  MouseEventParams,
  SeriesMarker,
  BusinessDay,
} from 'lightweight-charts';
import { BalanceHistory } from '@tradingblock/types';
import dayjs from 'dayjs';
import { ChartTooltip } from './ChartTooltip';

interface AnalyticsChartProps {
  dateRange: {
    start: Date;
    end: Date;
  };
  filteredBalances: BalanceHistory[];
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  dateRange,
  filteredBalances,
}: AnalyticsChartProps): JSX.Element => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chart = useRef<IChartApi | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const seriesRef = useRef<ISeriesApi<'Baseline'> | null>(null);
  const [tooltipData, setTooltipData] = useState<{
    point: { x: number; y: number };
    data: { date: string; price: number; historyItem?: BalanceHistory };
    visible: boolean;
  }>({
    point: { x: 0, y: 0 },
    data: { date: '', price: 0 },
    visible: false,
  });
  const [chartBounds, setChartBounds] = useState<DOMRect | null>(null);

  // Define the crosshair move handler
  const crosshairMoveHandler = (param: MouseEventParams) => {
    if (!param.time || !param.point || !seriesRef.current) {
      setTooltipData(prev => ({ ...prev, visible: false }));
      return;
    }

    const price = param.seriesPrices.get(seriesRef.current);
    if (price === undefined) {
      setTooltipData(prev => ({ ...prev, visible: false }));
      return;
    }

    const priceValue = typeof price === 'number' ? price : price.close;
    const hoveredDate =
      typeof param.time === 'number'
        ? dayjs.unix(param.time).format("D MMM 'YY")
        : dayjs(
            `${param.time.year}-${String(param.time.month).padStart(2, '0')}-${String(param.time.day).padStart(2, '0')}`
          ).format("D MMM 'YY");

    const historyItem = filteredBalances.find(item => dayjs(item.recordDate).format("D MMM 'YY") === hoveredDate);

    setTooltipData({
      point: { x: param.point.x, y: param.point.y },
      data: { date: hoveredDate, price: priceValue, historyItem },
      visible: true,
    });
  };

  useEffect(() => {
    // Create the chart only if the container is defined
    if (chartContainerRef.current) {
      chart.current = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        layout: {
          backgroundColor: 'rgb(3, 18, 55)',
          textColor: 'rgba(255, 255, 255, 0.9)',
          fontSize: 14,
        },
        grid: {
          vertLines: {
            visible: false,
          },
          horzLines: {
            visible: false,
          },
        },
        crosshair: {
          mode: CrosshairMode.Magnet,
        },
        rightPriceScale: {
          visible: true,
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
        },
      });

      // Add series
      if (chart.current) {
        seriesRef.current = chart.current.addBaselineSeries({
          baseValue: { type: 'price', price: 0 },
          lineWidth: 1,
          priceFormat: {
            type: 'price',
            precision: 2,
            minMove: 0.01,
          },
        });
      }

      // Resize observer to ensure the chart resizes properly
      resizeObserver.current = new ResizeObserver(entries => {
        if (chart.current) {
          const { width, height } = entries[0].contentRect;
          chart.current.applyOptions({ width, height });
          setTimeout(() => {
            if (chart.current) {
              chart.current.timeScale().fitContent();
            }
          }, 0);
        }
      });

      if (chartContainerRef.current) {
        resizeObserver.current.observe(chartContainerRef.current);
      }
    }

    // Cleanup function
    return () => {
      if (chart.current) {
        chart.current.remove();
        chart.current = null;
      }
      if (resizeObserver.current && chartContainerRef.current) {
        resizeObserver.current.unobserve(chartContainerRef.current);
        resizeObserver.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (filteredBalances.length && seriesRef.current !== null) {
      // Set the chart data
      const filteredBalanceSeries = filteredBalances.map(historyItem => ({
        time: historyItem.recordDate,
        value: historyItem.accountValue,
      }));
      seriesRef.current.setData(filteredBalanceSeries);

      // Add subtle markers for dividends and transfers
      const markers: SeriesMarker<Time>[] = [];

      filteredBalances.forEach(historyItem => {
        if (historyItem.cashieringTransfers) {
          historyItem.cashieringTransfers.forEach(() => {
            markers.push({
              time: historyItem.recordDate,
              position: 'aboveBar',
              color: 'rgba(255, 255, 255, 0.5)', // Subtle, neutral color
              shape: 'circle', // Small, minimal shape
              size: 0.2, // Small size to reduce visual impact
            });
          });
        }

        if (historyItem.dividends) {
          historyItem.dividends.forEach(() => {
            markers.push({
              time: historyItem.recordDate,
              position: 'aboveBar',
              color: 'rgba(255, 255, 255, 0.5)', // Subtle, neutral color
              shape: 'circle', // Small, consistent shape
              size: 0.2, // Keep it subtle
            });
          });
        }
      });

      seriesRef.current.setMarkers(markers);

      // Subscribe to crosshair move events
      if (chart.current) {
        chart.current.subscribeCrosshairMove(crosshairMoveHandler);
      }

      // Unsubscribe from crosshair move events when the component unmounts
      return () => {
        if (chart.current) {
          chart.current.unsubscribeCrosshairMove(crosshairMoveHandler);
        }
      };
    }
  }, [filteredBalances, dateRange]);

  useEffect(() => {
    if (chartContainerRef.current) {
      const updateBounds = () => {
        setChartBounds((chartContainerRef.current && chartContainerRef.current.getBoundingClientRect()) || null);
      };

      updateBounds();
      window.addEventListener('resize', updateBounds);
      return () => window.removeEventListener('resize', updateBounds);
    }
  }, []);

  return (
    <div style={{ position: 'relative', height: '400px', width: '100%' }}>
      <div ref={chartContainerRef} className="chart-container" style={{ width: '100%', height: '100%' }} />
      <ChartTooltip
        point={tooltipData.point}
        data={tooltipData.data}
        visible={tooltipData.visible}
        chartBounds={chartBounds || new DOMRect()}
      />
    </div>
  );
};
